import React from 'react'
import { T, RatingBadge, Avatar, Stat, VerdictChip, BET_VLEVEL, pct, probColor, confColor,
         fmtOdds, bookName, MARKET_LABEL } from './ui'

const STATUS = {
  active:       { color: T.green, label: '✓ Active' },
  questionable: { color: T.amber, label: '⚠ Questionable' },
  returning:    { color: T.amber, label: '↩ Returning' },
  out:          { color: T.red,   label: '✕ Out' },
}

// Plain-English read of the tier (the NFL card rule): what the badge MEANS in words,
// so a shiny % on a play the model has buried can't mislead. Early-season reads that
// still lean on last season say so.
export function plainRead(tier, goalMarket, carryover) {
  const MAP = {
    'ELITE':    "The model's top tier — its highest-confidence plays.",
    'BEST BET': "The model's top tier — its highest-confidence plays.",
    'STRONG':   'A confident lean from the model.',
    'PLAY':     'A modest lean — playable, not a smash.',
    'WATCH':    'Borderline — worth watching, not a strong play.',
    'PASS':     `A low-confidence ${goalMarket ? 'dart' : 'play'} — the model isn't buying it.`,
  }
  let s = MAP[tier] || MAP.PASS
  if (carryover) s += " Early-season read — still leaning on last season's numbers."
  return s
}

// Last-N sparkline: did he clear THIS line? Tall green = over, short faint = under,
// oldest -> newest.
export function HitBars({ hist }) {
  const h = hist && hist.hist
  if (!h || !h.length) return <span style={{ fontSize: 11, color: T.faint }}>no logs</span>
  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 2, height: 15 }}>
      {h.map((v, i) => (
        <span key={i} style={{ width: 3, height: v ? 14 : 6, borderRadius: 1,
          background: v ? T.green : T.faint, opacity: v ? 0.95 : 0.7 }} />
      ))}
    </span>
  )
}

function MiniCard({ label, children }) {
  return (
    <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 9,
      padding: '7px 10px', minWidth: 0 }}>
      <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.05em', marginBottom: 4,
        textTransform: 'uppercase' }}>{label}</div>
      {children}
    </div>
  )
}

// Matchup read from the model's clamped matchup group (opponent defense x goalie x
// pace): >1 = soft spot. Goalies' saves read the shots-faced workload instead.
export function matchupRating(p) {
  const g = p.components && p.components.matchup_group
  if (g == null) return { label: '—', color: T.muted }
  if (g >= 1.03) return { label: 'GOOD', color: T.green }
  if (g <= 0.97) return { label: 'TOUGH', color: T.red }
  return { label: 'FAIR', color: T.muted2 }
}

function CaseChip({ c }) {
  if (!c) return null
  const tally = `${c.greens}✓${c.reds > 0 ? ` ${c.reds}✕` : ''}`
  return <VerdictChip label={c.verdict} level={BET_VLEVEL[c.verdict] || 'neutral'} sub={tally}
    title={`The Case — ${c.verdict}: ${c.greens} for${c.reds ? `, ${c.reds} against` : ''}`} />
}

const chip = (color, filled) => ({ fontSize: 11, fontWeight: 800, color: filled ? '#04140D' : color,
  background: filled ? color : (color === T.green ? T.greenDim : 'transparent'),
  border: `1px solid ${color}`, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' })

/**
 * NHL play card — the NFL card layout: grade + name + headline %, the bet with its
 * verdict/edge/value chips, a plain-English read, the scan row (matchup · last-10 vs
 * THIS line · projection), confidence + line, the best price, and a status strip.
 * Honest line labeling stays: no posted book line -> the model's PROJECTED line with
 * an amber PROJ badge, never a book number that doesn't exist; a suspect line (feed
 * error) hides the edge/value with a warning instead.
 */
export function PickCard({ p, onClick, rank }) {
  const priced = p.book_line != null
  const line = priced ? p.book_line : p.model_line
  const isProj = !priced
  const prob = p.prob_over || 0
  const edge = p.edge_pct
  const conf = p.confidence || 0
  const marketLabel = MARKET_LABEL[p.market] || p.market
  const goalMarket = p.market === 'goals'
  // Unpriced cards show the model's CONVICTION; priced cards the edge-gated tier.
  const dispTier = priced ? p.tier : (p.conviction || p.tier)
  const st = STATUS[p.status] || STATUS.active
  const betText = goalMarket ? 'Anytime goal' : `Over ${line} ${marketLabel}`
  const mr = matchupRating(p)
  const hh = p.hit_hist

  return (
    <div onClick={onClick} style={{ background: T.card, border: `1px solid ${rank && rank <= 3 ? T.greenMid : T.border}`,
      borderRadius: 12, padding: 14, marginBottom: 8, cursor: onClick ? 'pointer' : 'default' }}>
      {/* header — grade, player, headline % */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5, gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <RatingBadge tier={dispTier} size={34} />
          <Avatar url={p.headshot} name={p.name} size={34} />
          <span style={{ fontSize: 16, fontWeight: 800, color: T.text, lineHeight: 1.2, minWidth: 0 }}>
            {p.name} <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, whiteSpace: 'nowrap' }}>{p.position}</span>
          </span>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 19, lineHeight: 1,
            color: probColor(prob, p.market) }}>{pct(prob)}</div>
          <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.04em' }}>
            {isProj ? 'MODEL %' : goalMarket ? 'TO SCORE' : 'TO HIT'}</div>
        </div>
      </div>

      {/* the bet + its chips */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 11.5, color: T.muted, minWidth: 0 }}>
          {p.team} · vs {p.opponent} · <b style={{ color: T.text }}>{betText}</b>
          {isProj && !goalMarket && (
            <span title="No posted book line yet — this is our PROJECTED line, not a book number"
              style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, letterSpacing: '0.04em', color: T.amber,
                background: T.amberDim, border: `1px solid ${T.amber}`, borderRadius: 4, padding: '1px 5px' }}>PROJ</span>
          )}
          {p.components?.pp_unit && (
            <span title="Power-play unit (inferred from PP ice time)" style={{ marginLeft: 6, fontSize: 10,
              fontWeight: 800, color: T.ice, border: `1px solid ${T.ice}66`, borderRadius: 4, padding: '1px 5px' }}>
              {p.components.pp_unit}</span>
          )}
          {Array.isArray(p.components?.intel) && p.components.intel.length > 0 && (
            <span title={p.components.intel.map(i => i.note).join(' · ')} style={{ marginLeft: 6, fontSize: 10,
              fontWeight: 800, color: T.purple, border: `1px solid ${T.purple}66`, borderRadius: 4, padding: '1px 5px' }}>
              {p.components.intel.some(i => i.source === 'scout') ? '🔭' : '📝'} INTEL</span>
          )}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {p.line_suspect && (
            <span title="This book's line disagrees with the rest of the market — likely a bad feed row, not a real edge"
              style={chip(T.amber)}>⚠ Line looks off</span>
          )}
          {!p.line_suspect && p.case && <CaseChip c={p.case} />}
          {!p.line_suspect && edge != null && Math.abs(edge) >= 0.5 && (
            <span style={chip(edge >= 0 ? T.green : T.red)}>{edge >= 0 ? '+' : ''}{Number(edge).toFixed(1)}% edge</span>
          )}
          {!p.line_suspect && p.value && <span style={chip(T.green, true)}>✓ Value</span>}
        </div>
      </div>

      {p.live_status && (
        <div style={{ marginBottom: 10 }}><LiveChip status={p.live_status} actual={p.live_actual} line={line} /></div>
      )}

      {/* plain-English read, colour-cued to the tier */}
      <div style={{ fontSize: 12, lineHeight: 1.35, marginBottom: 10,
        color: (dispTier === 'PASS' || dispTier === 'WATCH') ? T.amber
          : (dispTier === 'ELITE' || dispTier === 'BEST BET' || dispTier === 'STRONG') ? T.green : T.muted }}>
        {plainRead(dispTier, goalMarket, p.rate_source === 'carryover')}
      </div>

      {/* scan row — matchup · recent hits vs THIS line · projection */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 8 }}>
        <MiniCard label="Matchup"><span style={{ fontSize: 13, fontWeight: 800, color: mr.color }}>{mr.label}</span></MiniCard>
        <MiniCard label={`Last ${hh?.n || 10} · cleared`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <HitBars hist={hh} />
            {hh?.n ? <span style={{ fontSize: 12, fontFamily: T.mono, fontWeight: 700,
              color: probColor(hh.w / hh.n, p.market) }}>{hh.w}/{hh.n}</span> : null}
          </div>
        </MiniCard>
        <MiniCard label="Projection"><span style={{ fontSize: 13, fontWeight: 800, fontFamily: T.mono,
          color: T.text }}>{p.proj != null ? p.proj : '—'}</span></MiniCard>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginBottom: 10 }}>
        <Stat label="Confidence" value={conf} color={confColor(conf)} />
        <Stat label={goalMarket ? 'Market' : priced ? 'Book line' : 'Model line'}
          value={goalMarket ? 'Anytime' : line} />
      </div>

      {p.line_suspect && (
        <div style={{ fontSize: 11, color: T.amber, marginBottom: 2, lineHeight: 1.4 }}>
          ⚠ This book's line (O{p.book_line}) disagrees with the rest of the market — likely a bad feed row,
          not a real edge. Verify before trusting it.
        </div>
      )}
      {priced && !p.line_suspect && p.over_odds != null && (
        <div style={{ fontSize: 11, color: T.muted2, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ color: T.green, fontWeight: 700 }}>💰 Best {fmtOdds(p.over_odds)}</span>
          <span>{bookName(p.book)} · {betText}</span>
          <span style={{ color: T.faint }}>· tap for all books</span>
        </div>
      )}
      {isProj && (
        <div style={{ fontSize: 11, color: T.faint, marginBottom: 2 }}>
          Model estimate for this player · book line not posted yet
        </div>
      )}
      {p.note && p.status !== 'questionable' && p.status !== 'returning' && <div style={{ fontSize: 11, color: T.muted2, marginBottom: 2 }}>· {p.note}</div>}

      <div style={{ background: T.card2, borderRadius: 6, padding: '6px 10px', marginTop: 8, fontSize: 11,
        display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ color: st.color }}>{st.label}{p.injury ? ` — ${p.injury}` : p.status === 'returning' && p.note ? ` — ${p.note}` : ''}</span>
        <span style={{ color: T.muted }}>{p.market === 'saves' ? 'Starters confirm ~morning skate' : 'Lineups post ~morning skate'}</span>
      </div>
    </div>
  )
}

// Live in-game hit/miss: green ✓ the instant the total clears the line (locked),
// red ✗ once the game is final and it didn't, amber while in progress.
function LiveChip({ status, actual, line }) {
  const map = {
    hit:  { c: T.green, label: `${actual}/${line} ✓` },
    miss: { c: T.red,   label: `${actual}/${line} ✗` },
    live: { c: T.amber, label: `${actual}/${line} · live` },
  }
  const s = map[status] || map.live
  return (
    <div style={{ fontSize: 10.5, fontWeight: 800, color: s.c, marginTop: 4,
      fontFamily: T.mono }}>{s.label}</div>
  )
}

// The honest-line legend shown once above a board so a first-time viewer knows what
// PROJ means and why some plays have no edge number yet.
export function LineLegend() {
  return (
    <div style={{ fontSize: 10.5, color: T.muted, lineHeight: 1.5, margin: '2px 2px 10px' }}>
      <b style={{ color: T.amber }}>PROJ</b> = our projected line (no book number posted yet).
      Edge shown only when a real book price exists — the model beating the price is the Value.
    </div>
  )
}
